<template>
	<div class="flex flex-1">
		<div class="space-y-3 flex-1 min-w-0 max-w-100 overflow-scroll">
			<uiList
				:items="segments"
				:search="['notes']"
				:add="true"
				width="auto"
				@add="setNewForm"
			>
				<template #items="{ items: segments }">
					<div
						class="space-y-3 flex-1 min-w-0 px-5 pt-2 pb-6 overflow-scroll"
					>
						<div
							v-for="segment in segments"
							:key="segment.id"
							class="cursor-pointer space-y-1 rounded-2xl p-3"
							:class="{
								'bg-light-500': selected?.id !== segment.id,
								'bg-white shadow-lg':
									selected?.id === segment.id,
							}"
							@click="handleSelectSegment(segment)"
						>
							<p
								class="font-mono m-0 text-xs opacity-50 whitespace-pre-line"
							>
								{{ segment.id }}
							</p>

							<p
								v-if="segment.notes"
								class="m-0 text-sm whitespace-pre-line"
							>
								{{ segment.notes }}
							</p>
						</div>
					</div>
				</template>
			</uiList>
		</div>

		<div class="flex-1 min-w-0 max-w-200 p-8 overflow-auto">
			<div
				v-if="selected != null"
				class="space-y-3"
				:key="selected?.id ?? 'new'"
			>
				<uiTextarea v-model="selected.notes" label="Описание" />

				<div
					v-for="(field, i) in selected.fields"
					:key="i"
					class="flex space-x-3 items-center"
				>
					<div class="flex-1">
						<uiDropdown
							v-model="field.name"
							label="Поле"
							:options="fields"
							:reduce="item => item.value"
							clearSearchOnSelect
							class="flex-1"
							@input="onChangeField(field)"
						/>
					</div>

					<div class="flex-1">
						<uiDropdown
							v-model="field.operator"
							label="Условие"
							:options="field.operators"
							:reduce="item => item.value"
							clearSearchOnSelect
							class="flex-1"
							@input="onChangeOperator(field)"
						/>
					</div>

					<div class="flex-1">
						<uiDropdown
							v-show="Array.isArray(field.conditions)"
							v-model="field.conditionValue"
							label="Вариант"
							:options="field.conditions"
							:reduce="item => item.value"
							:multiple="['$in', '$nin'].includes(field.operator)"
							clearSearchOnSelect
							class="flex-1"
						/>

						<div
							v-show="field.conditions === 'date'"
							class="flex-1"
						>
							<p class="m-0 text-sm mb-1 opacity-50">Дата</p>
							<DatePicker
								v-model="field.conditionValue"
								type="date"
								format="DD.MM.YYYY"
							/>
						</div>

						<uiInput
							v-show="field.conditions === 'string'"
							v-model="field.conditionValue"
							label="Строка"
						/>

						<uiInput
							v-show="field.conditions === 'number'"
							v-model="field.conditionValue"
							label="Число"
							mask="99999"
						/>
					</div>

					<div class="flex pt-6 w-14">
						<div
							v-if="selected.fields.length > 1"
							class="cursor-pointer opacity-30 transition-all inline-flex hover:opacity-100"
							@click="removeFields(i)"
						>
							<Icon icon="ph:trash" height="20" />
						</div>

						<div
							v-if="i + 1 === selected.fields.length"
							class="cursor-pointer ml-auto opacity-30 transition-all inline-flex hover:opacity-100"
							@click="addNewFields"
						>
							<Icon icon="ph:plus-circle" height="20" />
						</div>
					</div>
				</div>

				<div class="flex pt-3 pb-3 justify-between">
					<div>
						<uiButton
							v-if="selected?.id"
							class="px-5"
							color="red"
							:loading="remove.isPending"
							@click="remove()"
						>
							Удалить
						</uiButton>
					</div>

					<uiButton
						class="px-5"
						:loading="save.isPending"
						@click="save()"
						:disabled="!canSave"
					>
						{{ selected?.id ? 'Сохранить' : 'Создать' }}
					</uiButton>
				</div>

				<div
					v-if="selected?.id"
					class="border-solid border-t-[#eee] border-0 border-t-2 pt-6 pb-3"
				>
					<div class="flex justify-between items-center">
						<div>
							<p class="m-0 text-sm">
								{{ users.length === 1 ? 'Найден' : 'Найдено' }}
								{{ formatWithSpaces(users.length) }}
								{{
									pluralize(
										users.length,
										'пользователь',
										'пользователя',
										'пользователей'
									)
								}}
							</p>
						</div>

						<uiButton
							class="ml-auto px-5"
							color="grey"
							:loading="getUsers.isPending"
							@click="getUsers()"
						>
							Обновить список
						</uiButton>
					</div>

					<div v-if="users.length > 0" class="mt-3">
						<virtual-list
							:data-key="'_id'"
							:data-sources="users"
							:data-component="VirtualListUser"
							:estimate-size="20"
							class="max-h-50 overflow-auto"
						/>
					</div>
				</div>

				<div
					v-if="users.length > 0"
					class="border-solid border-t-[#eee] space-y-3 border-0 border-t-2 flex-1 pt-6 pb-3 justify-between items-center"
				>
					<div class="flex space-x-3">
						<uiInput v-model="push.title" label="Заголовок" />
						<uiInput v-model="push.body" label="Описание" />
					</div>

					<div class="flex space-x-3 items-end">
						<uiDropdown
							v-model="push.openModal"
							label="При клике на пуш открыть"
							:options="payloadList"
							:reduce="item => item.value"
							class="flex-1"
							clearSearchOnSelect
							@input="onChangeOpenModal"
						/>

						<div class="flex flex-1">
							<uiInput
								v-if="payloadMap[push.openModal]?.paramKind === 'url'"
								v-model="push.currentModalParam"
								label=" "
								placeholder="https://docs.lapka.pet/s/..."
								class="flex-1"
							/>

							<uiDropdown
								v-else-if="payloadMap[push.openModal]?.needsParam"
								v-model="push.currentModalParam"
								label=" "
								:options="payloadMap[push.openModal].items"
								:reduce="item => item.value"
								class="flex-1"
								clearSearchOnSelect
							/>
						</div>

						<uiButton
							class="ml-auto mb-1 px-5 w-30"
							:disabled="!canSendPush"
							:loading="sendPush.isPending"
							@click="sendPush()"
						>
							Отправить пуш
						</uiButton>
					</div>

					<div v-if="sentPushStatistic != null" class="text-sm">
						<p class="m-0 pb-1">
							Разослалили пуши, немного статистики 👇
						</p>
						<p class="m-0">
							Пользователей: {{ sentPushStatistic.users }}
						</p>
						<p class="m-0">
							Токенов: {{ sentPushStatistic.tokens }}
						</p>
						<p class="m-0">
							Успешных отправок:
							{{ sentPushStatistic.successCount }}
						</p>
						<p class="m-0">
							Неуспешных отправок:
							{{ sentPushStatistic.failureCount }}
						</p>
					</div>
				</div>

				<!-- <pre>selcted: {{ selected }}</pre> -->
			</div>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { pluralize } from 'numeralize-ru'
import DatePicker from 'vue2-datepicker'
import VirtualList from 'vue-virtual-scroll-list'
import VirtualListUser from '@/components/VirtualListUser'
import api from '@/utils/ky'
import { API } from '@/config'
import { useAsyncMethod } from '@/utils/async-methods'
import { confirm, notify } from '@/utils/events'
import { getFullname } from '@/utils/name'
import { useStorage } from '@vueuse/core'
import { formatWithSpaces } from '@/utils/number-format'
import { fetchArticles } from '@/api'

const fields = [
	{
		value: 'auth.sessions.pushType',
		label: 'Устройство',
		operators: ['$eq', '$ne'],
		conditions: [
			{
				value: 'apn',
				label: 'iOS',
			},
			{
				value: 'fcm',
				label: 'Android',
			},
		],
	},
	{
		value: 'auth.sessions.updated',
		label: 'Последний вход',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'date',
	},
	{
		value: 'auth.sessions.appVersion.name',
		label: 'Версия приложения',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'string',
	},
	{
		value: 'createdAt',
		label: 'Дата регистрации',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'date',
	},
	{
		value: 'phone',
		label: 'Телефон',
		operators: ['$eq', '$ne', '$in'],
		conditions: 'string',
	},
	{
		value: 'pets.length',
		label: 'Количество питомцев',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'number',
	},
	{
		value: 'roles',
		label: 'Роль',
		operators: ['$in', '$nin'],
		conditions: [
			{
				value: 'god',
				label: 'god',
			},
			{
				value: 'admin',
				label: 'admin',
			},
			{
				value: 'support',
				label: 'support',
			},
			{
				value: 'medic',
				label: 'medic',
			},
			{
				value: 'client',
				label: 'client',
			},
			{
				value: 'love',
				label: 'love',
			},
			{
				value: 'volunteer',
				label: 'volunteer',
			},
		],
	},
	{
		value: 'storiesSeen.length',
		label: 'Количество просмотренных сторис',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'number',
	},
	{
		value: 'storiesSeen',
		label: 'Просмотренные сторис',
		operators: ['$in', '$nin'],
		conditions: [],
	},
	{
		value: 'pets.kind',
		label: 'Тип питомца',
		operators: ['$eq', '$ne'],
		conditions: [
			{
				value: 'dog',
				label: '🐶 Собака',
			},
			{
				value: 'cat',
				label: '🐱 Кошка',
			},
		],
	},
	{
		value: 'pets.gender',
		label: 'Пол питомца',
		operators: ['$eq', '$ne'],
		conditions: [
			{
				value: 'male',
				label: 'Мальчик',
			},
			{
				value: 'female',
				label: 'Девочка',
			},
		],
	},
	{
		value: 'pets.weight',
		label: 'Вес питомца',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'number',
	},
	{
		value: 'pets.birth',
		label: 'Возраст питомца',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'date',
	},
	{
		value: 'pets.sterilized',
		label: 'Стерилизация питомца',
		operators: ['$eq', '$ne'],
		conditions: [
			{
				value: true,
				label: 'Да',
			},
			{
				value: false,
				label: 'Нет',
			},
		],
	},
	{
		value: 'pets.home',
		label: 'Питомец живет',
		operators: ['$in', '$nin'],
		conditions: [
			{
				value: 'apartment',
				label: 'В квартире',
			},
			{
				value: 'house',
				label: 'Дома',
			},
			{
				value: 'outdoor',
				label: 'На улице',
			},
			{
				value: 'crate',
				label: 'В вольере',
			},
		],
	},
	{
		value: 'pets.food',
		label: 'Корм питомца',
		operators: ['$in', '$nin'],
		conditions: [
			{
				value: 'dry',
				label: 'Dry',
			},
			{
				value: 'wet',
				label: 'Wet',
			},
			{
				value: 'raw',
				label: 'Raw',
			},
			{
				value: 'semimoist',
				label: 'Semi-moist',
			},
			{
				value: 'diet',
				label: 'Diet',
			},
			{
				value: 'mixed',
				label: 'Mixed',
			},
		],
	},
	{
		value: 'pets.importantDates.kind',
		label: 'Напоминания питомца',
		operators: ['$eq', '$ne'],
		conditions: [
			{
				value: 'complexVaccination',
				label: 'О комплексной вакцинации',
			},
			{
				value: 'rabiesVaccination',
				label: 'Об обработке от глистов',
			},
			{
				value: 'parasiteTreatment',
				label: 'Об обработке от паразитов',
			},
			{
				value: 'acariTreatment',
				label: 'Об обработке от клещей',
			},
			{
				value: 'foodDiaryReminder',
				label: 'О дневнике питания',
			},
		],
	},
	{
		value: 'documents.length',
		label: 'Количество документов',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'number',
	},
	{
		value: 'documents.kind',
		label: 'Тип документа',
		operators: ['$in', '$nin'],
		conditions: [
			{
				value: 'prescription',
				label: 'Prescription',
			},
			{
				value: 'vetBill',
				label: 'Vet bill',
			},
			{
				value: 'labTest',
				label: 'Lab test',
			},
			{
				value: 'passport',
				label: 'Passport',
			},
			{
				value: 'treatmentPlan',
				label: 'Treatment plan',
			},
			{
				value: 'ultrasound',
				label: 'Ultrasound',
			},
			{
				value: 'ctScan',
				label: 'CT scan',
			},
			{
				value: 'mri',
				label: 'MRI scan',
			},
			{
				value: 'xray',
				label: 'X-Ray scan',
			},
			{
				value: 'other',
				label: 'Other',
			},
		],
	},
	{
		value: 'events.length',
		label: 'Количество событий',
		operators: ['$eq', '$ne', '$gt', '$gte', '$lt', '$lte'],
		conditions: 'number',
	},
]

const operatorsMap = {
	$eq: 'Равно',
	$ne: 'Не равно',
	$gt: 'Больше',
	$gte: 'Больше или равно',
	$lt: 'Меньше',
	$lte: 'Меньше или равно',
	$in: 'Содержит',
	$nin: 'Не содержит',
}

const payloadMap = {
	Chat: {
		value: 'Chat',
		label: 'Чат',
		getPayload: () =>
			JSON.stringify({
				openModal: 'Chat',
			}),
	},
	PetProfile: {
		value: 'PetProfile',
		label: 'Профиль питомца',
		getPayload: () =>
			JSON.stringify({
				openModal: 'PetProfile',
			}),
	},
	DocsArchive: {
		value: 'DocsArchive',
		label: 'Архив документов',
		getPayload: () =>
			JSON.stringify({
				openModal: 'DocsArchive',
			}),
	},
	PetMedia: {
		value: 'PetMedia',
		label: 'Фотки',
		getPayload: () =>
			JSON.stringify({
				openModal: 'PetMedia',
			}),
	},
	PetScanner: {
		value: 'PetScanner',
		label: 'Порода по фото',
		getPayload: () =>
			JSON.stringify({
				openModal: 'PetScanner',
			}),
	},
	PetReminders: {
		value: 'PetReminders',
		label: 'Напоминания',
		getPayload: () =>
			JSON.stringify({
				openModal: 'PetReminders',
			}),
	},
	FoodDiary: {
		value: 'FoodDiary',
		label: 'Дневник питания',
		getPayload: () =>
			JSON.stringify({
				openModal: 'FoodDiary',
			}),
	},
	UsefullTemplates: {
		value: 'UsefullTemplates',
		label: 'Полезные шаблоны',
		getPayload: () =>
			JSON.stringify({
				openModal: 'UsefullTemplates',
			}),
	},
	CanPetEat: {
		value: 'CanPetEat',
		needsParam: true,
		label: 'Опасные продукты',
		items: [
			{
				value: 'dog',
				label: '🐶 Собака',
			},
			{
				value: 'cat',
				label: '🐱 Кошка',
			},
		],
		getPayload: kind =>
			JSON.stringify({
				openModal: 'CanPetEat',
				props: { kind },
			}),
	},
	PetFriendlyMap: {
		value: 'PetFriendlyMap',
		label: 'Петфрендли места',
		getPayload: () =>
			JSON.stringify({
				openModal: 'PetFriendlyMap',
				options: { draggable: false },
			}),
	},
	Stories: {
		value: 'Stories',
		needsParam: true,
		label: 'Сторис',
		items: [],
		getPayload: id =>
			JSON.stringify({
				openFullscreen: 'Stories',
				props: { id },
			}),
	},
	Articles: {
		value: 'Articles',
		needsParam: true,
		label: 'Страница статей',
		items: [],
		getPayload: id =>
			JSON.stringify({
				openModal: 'Articles',
				props: { id },
			}),
	},
	WebView: {
		value: 'WebView',
		needsParam: true,
		label: 'Статья',
		items: [],
		getPayload: url =>
			JSON.stringify({
				openModal: 'WebView',
				props: { url },
			}),
	},
	WebViewUrl: {
		value: 'WebViewUrl',
		needsParam: true,
		paramKind: 'url',
		label: 'Статья по ссылке',
		getPayload: url =>
			JSON.stringify({
				openModal: 'WebView',
				props: { url },
			}),
	},
}

export default {
	components: {
		DatePicker,
		VirtualList,
		VirtualListUser,
	},
	setup() {
		const segments = ref([])
		function setSegments(newSegments) {
			segments.value = newSegments.map(segment => {
				return {
					...segment,
					fields: segment.fields.map(f => {
						const foundField = fields.find(
							field => field.value === f.name
						)
						const { conditions, operators } = foundField ?? {}
						let conditionValue = f.condition ?? null

						if (
							conditions === 'string' &&
							['$in', '$nin'].includes(f.operator) &&
							Array.isArray(f.condition)
						) {
							conditionValue = f.condition.join(', ')
						}

						if (conditions === 'date' && f.condition) {
							conditionValue = new Date(f.condition)
						}

						return {
							name: f.name,
							operator: f.operator,
							operators:
								operators?.map(operator => ({
									value: operator,
									label: operatorsMap[operator],
								})) ?? [],
							conditionValue,
							conditions,
						}
					}),
				}
			})
		}

		onMounted(async () => {
			const { data: loadedSegments } = await api.get(API.SEGMENTS.list)
			setSegments(loadedSegments)

			const { data: loadedStories } = await api.get(API.STORIES.list)

			const preparedStories = loadedStories.map(s => {
				return {
					value: s.id,
					label: `[${s.country.toUpperCase()}/${s.lang.toUpperCase()}] ${
						s.title
					}`,
				}
			})

			const fieldWithStoriesList = fields.find(
				f => f.value === 'storiesSeen'
			)
			if (fieldWithStoriesList) {
				fieldWithStoriesList.conditions = preparedStories
			}

			payloadMap.Stories.items = preparedStories

			const { data: pages } = await api.get('admin/v2/articles/pages')

			payloadMap.Articles.items = pages.map(page => ({
				value: page.id,
				label: `[${page.country.toUpperCase()}/${page.lang.toUpperCase()}] ${
					page.title
				}`,
			}))

			const articles = await fetchArticles()

			payloadMap.WebView.items = articles.map(a => ({
				value: a.url,
				label: a.documentTitle,
			}))
		})

		const selected = ref(null)

		const remove = useAsyncMethod(async () => {
			const confirmed = await confirm('Точно удаляем?')
			if (!confirmed) return

			const { data } = await api.post(API.SEGMENTS.delete, {
				id: selected.value?.id,
			})

			setSegments(data.segments)
			selected.value = null
		})

		const sentPushStatistic = ref(null)

		const users = ref([])
		watch(users, () => {
			sentPushStatistic.value = null
		})
		const getUsers = useAsyncMethod(async () => {
			users.value = []

			const { data } = await api.post(API.SEGMENTS.users, {
				id: selected.value?.id,
			})
			users.value = data
		})

		function handleSelectSegment(segment) {
			selected.value = structuredClone(segment)
			users.value = []
		}

		const canSave = computed(() => {
			return [
				selected.value?.fields.length > 0,
				selected.value?.fields?.every(f => f.conditionValue != null),
			].every(Boolean)
		})

		const save = useAsyncMethod(async () => {
			if (!canSave.value) return

			const payload = {
				...selected.value,
				fields: selected.value.fields.map(f => {
					let condition = f.conditionValue ?? null

					if (
						f.conditions === 'string' &&
						['$in', '$nin'].includes(f.operator)
					) {
						condition = f.conditionValue.split(', ')
					}

					if (f.conditions === 'number') {
						condition = Number(f.conditionValue)
					}

					return {
						name: f.name,
						operator: f.operator,
						condition,
					}
				}),
			}

			if (selected.value?.id) {
				const { data } = await api.post(API.SEGMENTS.update, payload)
				setSegments(data.segments)
			} else {
				const { data } = await api.post(API.SEGMENTS.create, payload)
				if (data.err) return notify(data.err)

				setSegments(data.segments)
				handleSelectSegment(segments.value.at(0))
			}

			await getUsers()
		})

		function setNewForm() {
			selected.value = {
				notes: '',
				fields: [
					{
						name: '',
						operator: '',
						operators: [],
						conditionValue: null,
						conditions: null,
					},
				],
			}
			users.value = []
		}

		function addNewFields() {
			selected.value.fields.push({
				name: '',
				operator: '',
				conditionValue: null,
				conditions: null,
			})
		}

		function removeFields(index) {
			selected.value.fields = selected.value.fields.filter(
				(_, i) => i !== index
			)
		}

		function onChangeField(field) {
			const foundField = fields.find(f => f.value === field.name)
			const { conditions, operators } = foundField ?? {}

			field.conditionValue = null
			field.conditions = conditions
			field.operator = null
			field.operators =
				operators?.map(operator => ({
					value: operator,
					label: operatorsMap[operator],
				})) ?? []
		}

		function onChangeOperator(field) {}

		const payloadList = [
			{ value: null, label: '–' },
			...Object.values(payloadMap).map(({ value, label }) => ({
				value,
				label,
			})),
		]

		const push = useStorage('push-data', {
			title: '',
			body: '',
			openModal: null,
			modalParams: [],
			currentModalParam: null,
		})
		// a target that carries a parameter (an article, a story) is useless
		// without it — the app would open an empty screen
		const canSendPush = computed(() => {
			if (!push.value.title || !push.value.body) return false

			const payloadItem = payloadMap[push.value.openModal]
			if (payloadItem?.needsParam && !push.value.currentModalParam) {
				return false
			}

			if (
				payloadItem?.paramKind === 'url' &&
				!/^https:\/\/\S+$/.test(push.value.currentModalParam ?? '')
			) {
				return false
			}

			return true
		})

		const sendPush = useAsyncMethod(async () => {
			const payloadItem = payloadMap[push.value.openModal]
			const payload = payloadItem?.needsParam
				? payloadItem.getPayload(push.value.currentModalParam)
				: payloadItem?.getPayload()

			const { data } = await api.post(API.SEGMENTS.push, {
				segmentId: selected.value?.id,
				title: push.value.title,
				body: push.value.body,
				payload,
			})
			sentPushStatistic.value = data
		})

		function onChangeOpenModal(val) {
			const payload = payloadMap[val]

			push.value.currentModalParam =
				payload?.paramKind === 'url' ? '' : payload?.items?.at(0)?.value
			push.value.modalParams = payload?.items ?? []
		}

		return {
			pluralize,
			getFullname,
			fields,
			segments,
			selected,
			handleSelectSegment,
			canSave,
			save,
			remove,
			users,
			getUsers,

			setNewForm,
			addNewFields,
			removeFields,
			onChangeField,
			onChangeOperator,

			push,
			sentPushStatistic,
			sendPush,
			canSendPush,

			VirtualListUser,
			formatWithSpaces,

			payloadMap,
			payloadList,
			onChangeOpenModal,
		}
	},
}
</script>
